import React, { useState, useRef, useEffect } from 'react'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import cn from 'classnames'
import AnimateHeight from 'react-animate-height'
import ReCAPTCHA from 'react-google-recaptcha'
import zxcvbn from 'zxcvbn'

import UserUtils from '@utils/UserUtils'
import useUser from '@hooks/useUser'
import Api from '@api'
import { ResetPasswordPayloadType } from '@custom-types/Auth'

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {query} = context;
  const {d} = query;
  let data = null;
  if (d) {
    data = JSON.parse(Buffer.from(d as string, 'base64').toString('utf-8'))
  }

  return {
    props: {data}
  }
}

interface ResetPasswordProps {
  data: {email: string, token: string} | null
}


const ResetPassword = ({data}: ResetPasswordProps) => {
  const { user } = useUser({ redirectIfFound: false })
  const router = useRouter();
  const emailRef = useRef<HTMLInputElement>(null);
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [tempToken, setTempToken] = useState<string>();
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [resetError, setResetError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [sendSuccess, setSendSuccess] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordStrength, setNewPasswordStrength] = useState<number>(-1);
  const [newPasswordVisible, setNewPasswordVisible] = useState<boolean>(false);
  const [newPasswordError, setNewPasswordError] = useState<boolean>(false); 
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState<boolean>(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>();
  const [resetSuccess, setResetSuccess] = useState<boolean>(false);
  const [siteKey, setSiteKey] = useState<string>(process.env.NEXT_PUBLIC_RECAPTCHA_KEY || '');
  //console.log('data', btoa(JSON.stringify({email: 'jerick@magpie.im', token: '1234'})))

  /* useEffect(() => {
    let timer: NodeJS.Timeout;
    if (router) {
      if (user && user.isLoggedIn) {
        const activated = user.team?.activated || false;
        if (activated) {
          timer = setTimeout(() => {
            router.replace('/dashboard');
          }, 500)
        } else {
          timer = setTimeout(() => {
            router.replace('/test/dashboard');
          }, 500)
        }
      }
    }

    return () => {
      if (timer !== null) clearTimeout(timer); 
    }
  }, [router, user]) */

  useEffect(() => {
    if (data) {
      setEmail(data.email);
      setTempToken(data.token);
    }
  }, [data])

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(false);
  }

  const onForgotPassword = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setResetError(undefined);

    if (!email) {
      setEmailError(true);
      emailRef?.current?.focus();
    } else if (!UserUtils.isValidEmail(email)) {
      setEmailError(true);
      emailRef?.current?.focus();
    } else {
      // both email and password has values
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setTimeout(async () => {
          //setResetError('We couldn\'t find that email. Please try again.');
          setVerifying(true);
          const token = await captchaRef?.current?.executeAsync();
          captchaRef?.current?.reset();
          console.log('recaptcha', token);
          if (email && token) {
            const forgotRes = await Api.sendForgotPasswordEmail({email, recaptcha: token});
            if (forgotRes.ok) {
              console.log('forgot_password', forgotRes.data);
              setVerifying(false);
              setSendSuccess(true);
            } else {
              setVerifying(false);
              setSendSuccess(false);
              setResetError(forgotRes.data?.message || 'Unable to process request this time. Try again later.')
            }
          }
        }, 200);
      }, 1000);
    }
  }

  const onResendClick = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setResetError(undefined);
    setSendSuccess(false);
    setVerifying(true);

    const token = await captchaRef?.current?.executeAsync();
    captchaRef?.current?.reset();
    console.log('recaptcha', token);
    if (email && token) {
      const forgotRes = await Api.sendForgotPasswordEmail({email, recaptcha: token});
      if (forgotRes.ok) {
        console.log('forgot_password', forgotRes.data);
        setVerifying(false);
        setSendSuccess(true);
      } else {
        setVerifying(false);
        setSendSuccess(false);
        setResetError(forgotRes.data.message)
      }
    }
  }

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    if (value.length >= 10) {
      const strength = zxcvbn(value, [email]);
      //console.log(strength);
      //console.log('strength', strength.score);
      setNewPasswordStrength(strength.score);
    } else {
      setNewPasswordStrength(-1);
    }
  }
  
  const onTogglePasswordVisible = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setNewPasswordVisible(!newPasswordVisible);
  }

  const onConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value !== newPassword) {
      setConfirmPasswordError('This password doesn\'t match. Try again.');
    } else {
      setConfirmPasswordError(undefined);
    }
  }
  
  const onToggleConfirmPasswordVisible = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setConfirmPasswordVisible(!confirmPasswordVisible);
  }

  const onResetPassword = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setResetSuccess(false);

    if (newPasswordStrength > 2 && newPassword === confirmPassword) {
      setLoading(true);
      if (tempToken && newPassword) {
        const params: ResetPasswordPayloadType = {
          new_password: newPassword,
          confirm_password: newPassword,
          token: tempToken
        };
        const response = await Api.resetPass(params);
        if (response.ok) {
          setLoading(false);
          setResetSuccess(true);
        } else {
          setLoading(false);
          setResetError(response.data.message || 'Something went wrong. Try again.')
        }
      }
    } 
  }

  const onContinueToSignIn = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    router.push('/login');
  }

  if (email && tempToken) {
    return (
      <div className="h-screen bg-white">
        <Head>
          <title>Magpie: Reset Password</title>
          <link rel="stylesheet" href="/css/auth.css"/>
        </Head>
        <div className="h-full min-h-full bg-white flex flex-col">
          {/* Background */}
          <div>
            <div className="fixed left-0 right-0 -top-62 bottom-0 z-0 transform -skew-y-12">
              <div className="absolute -top-250 left-0 right-0 origin-0-50 h-425 bg-offset"></div>
            </div>
          </div>
          {/* Content */}
          <div className="my-0 mx-4 z-0 relative flex flex-row h-full pt-14 justify-center">
            <div className="flex flex-col w-96 desktop:w-1/2 desktop:min-w-1/2 ultraWide:w-135 ultraWide:min-w-135">
              <div className="pb-10 pl-5">
                <img className="h-10 w-auto" src="https://client-objects.s3-us-west-2.amazonaws.com/magpie/magpie-logo-brand.svg" alt="magpie" />
              </div>
              <div className="flex-shrink-0 bg-white overflow-hidden shadow-card-lg rounded">
                <div className="py-8 px-0 desktop:py-14 desktop:px-12">
                  <div>
                    <div className="px-5">
                      {
                        resetSuccess ? (
                          <div role="main">
                            <div className="w-full pb-3 pointer-events-none">
                              <div className="flex flex-col justify-start flex-nowrap -ml-6 -mt-6">
                                <div className="mt-6 ml-6 pointer-events-auto">
                                  <div className="pointer-events-none">
                                    <div className="flex flex-col justify-start flex-nowrap -ml-2 -mt-2">
                                      <div className="mt-2 ml-2 pointer-events-auto">
                                        <span className="inline text-base font-medium leading-6 proportional-nums whitespace-normal">
                                          <div className="text-2xl font-bold leading-8 tracking-tight text-body">
                                            <span>You've successfully changed your password</span>
                                          </div>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-6 ml-6 pointer-events-auto">
                                  <button className="py-2 px-4 rounded min-h-11 h-full w-full inline-flex cursor-pointer text-base leading-7 font-medium text-white border-none bg-magpie-500 focus:outline-none transition duration-250 ease-linear focus:ring focus:ring-opacity-50 focus:ring-magpie-200" onClick={onContinueToSignIn}>
                                      <div className="flex align-baseline flex-row-reverse w-full relative">
                                        <div className="flex align-baseline flex-row-reverse justify-center w-full leading-0 flex-1-auto">
                                          <span className="block text-white text-base font-medium leading-7 proportional-nums whitespace-nowrap transition duration-250 ease-linear">Continue to Sign in</span>
                                        </div>
                                      </div>
                                    </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div role="main">
                            <div className="w-full pb-3 pointer-events-none">
                              <div className="flex flex-col justify-start flex-nowrap">
                                <div className="pointer-events-auto">
                                  <div className="pointer-events-none">
                                    <div className="flex flex-col justify-start flex-nowrap -ml-2 -mt-2">
                                      <div className="mt-2 ml-2 pointer-events-auto">
                                        <span className="text-gray-900 inline text-base font-medium leading-6 proportional-nums whitespace-normal">
                                          <div className="mb-5">
                                            <div className="text-2xl font-bold leading-8 tracking-tight text-body">
                                              <span>Reset your password</span>
                                            </div>
                                          </div>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="pointer-events-auto">
                                  <div className="flex flex-col justify-start flex-nowrap -ml-3 -mt-3">
                                    <div className="mt-3 ml-3 pointer-events-auto">
                                      <label htmlFor="password">
                                        <span className="cursor-pointer inline text-sm font-medium leading-5 proportional-nums whitespace-normal">New password</span>
                                      </label>
                                    </div>
                                    <div className="mt-3 ml-3 pointer-events-auto">
                                      <div className="min-h-22.5">
                                        <div className="min-h-21">
                                          <div className="relative">
                                            <div className="flex items-start">
                                              <div className="w-full">
                                                <input id="new-password" name="new-password" type={ newPasswordVisible ? "text" : "password"} autoComplete="new-password" aria-invalid={newPasswordStrength < 3} className={cn("inline-flex w-full max-w-full pr-14 cursor-text rounded min-h-9 text-gray-900 text-sm bg-white border focus:outline-none focus:ring focus:ring-opacity-50 transition duration-250 ease-in-out", {
                                                  "border-gray-200 focus:border-magpie-300 focus:ring-magpie-200": newPasswordStrength === -1 || newPasswordStrength > 2,
                                                  "border-red-500 focus:border-red-500 focus:ring-red-300": newPasswordStrength > -1 && newPasswordStrength < 3
                                                })} value={newPassword} onChange={onPasswordChange} />
                                              </div>
                                            </div>
                                            <button disabled={!newPassword} type="button" className={cn("flex absolute inset-y-0 right-0 pr-3 items-center text-gray-400 hover:text-gray-700 transition duration-250 ease-in-out focus:outline-none", {
                                              "opacity-100 cursor-pointer": newPassword,
                                              "opacity-0 cursor-default": !newPassword
                                            })} onClick={onTogglePasswordVisible}>
                                              {
                                                newPasswordVisible ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd"></path><path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z"></path></svg> : <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path></svg>
                                              }
                                            </button>
                                          </div>
                                          <div className="flex flex-col">
                                            <div className="basis-11">
                                              <div className={cn("min-h-4.5 transition-opacity duration-200 leading-3 pt-2", {
                                                  "opacity-0": !newPassword,
                                                  "opacity-100": newPassword
                                                })}>
                                                {/* Password strength description */}
                                                <span className={cn("inline text-xs proportional-nums whitespace-normal", {
                                                  "text-gray-700": newPasswordStrength === -1 || newPasswordStrength > 3,
                                                  "text-red-500": newPasswordStrength > -1 && newPasswordStrength < 3,
                                                  "text-yellow-500": newPasswordStrength === 3,
                                                  "text-green-600": newPasswordStrength > 3
                                                })}>
                                                  {
                                                    newPasswordStrength === -1 && <span>Your password needs to be at least 10 characters. Include multiple words and phrases to make it secure.</span>
                                                  }
                                                  {
                                                    newPasswordStrength > -1 && newPasswordStrength < 3 && <span>Your password is not strong enough. Avoid using a single sequence of characters (e.g., abcdefgh or 12345678).</span>
                                                  }
                                                  {
                                                    newPasswordStrength === 3 && <span>Your password works, but could be stronger. Try making it longer. </span>
                                                  }
                                                  {
                                                    newPasswordStrength > 3 && <span>Nice work! Your password is good.</span>
                                                  }
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="pointer-events-auto">
                                  <div className="h-full">
                                    <div className="pointer-events-none">
                                      <div className="flex flex-col justify-start flex-nowrap -ml-3 -mt-3">
                                        <div className="mt-3 ml-3 pointer-events-auto">
                                          <label htmlFor="confirmPassword">
                                            <span className="cursor-pointer inline text-sm font-medium leading-5 proportional-nums whitespace-normal">Confirm your password</span>
                                          </label>
                                        </div>
                                        <div className="mt-3 ml-3 pointer-events-auto">
                                          <div className="w-full relative">
                                            <input id="confirm-password" name="confirm-password" type={ confirmPasswordVisible ? "text" : "password"} autoComplete="new-password" aria-invalid={!!confirmPasswordError} className={cn("inline-flex w-full max-w-full pr-14 cursor-text rounded min-h-9 text-gray-900 text-sm bg-white border focus:outline-none focus:ring focus:ring-opacity-50 transition duration-250 ease-in-out", {
                                              "border-gray-200 focus:border-magpie-300 focus:ring-magpie-200": !confirmPasswordError,
                                              "border-red-500 focus:border-red-500 focus:ring-red-300": confirmPasswordError
                                            })} value={confirmPassword} onChange={onConfirmPasswordChange} />
                                            <button disabled={!confirmPassword} type="button" className={cn("flex absolute inset-y-0 right-0 pr-3 items-center text-gray-400 hover:text-gray-700 transition duration-250 ease-in-out focus:outline-none", {
                                              "opacity-100 cursor-pointer": confirmPassword,
                                              "opacity-0 cursor-default": !confirmPassword
                                            })} onClick={onToggleConfirmPasswordVisible}>
                                              {
                                                confirmPasswordVisible ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd"></path><path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z"></path></svg> : <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path></svg>
                                              }
                                            </button>
                                          </div>
                                          <div className={cn("block pt-2 transition-opacity duration-250", {
                                            "opacity-0": !confirmPasswordError,
                                            "opacity-100": confirmPasswordError
                                          })}>
                                            <div role="alert" className="flex flex-row">
                                              <div className="flex items-baseline flex-row pr-1 relative">
                                                <div className="flex items-baseline flex-row justify-start flex-1-auto leading-0">
                                                  <div className="flex text-red-500">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                                                  </div>
                                                </div>
                                              </div>
                                              <span className="text-red-500 inline text-xs proportional-nums whitespace-normal">{confirmPasswordError}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="pointer-events-auto">
                                  <div className="mt-12 flex flex-col">
                                    <button type="submit" disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword} className={cn("py-2 px-4 rounded min-h-11 h-full w-full inline-flex text-base leading-7 font-medium text-white border-none bg-magpie-500 focus:outline-none transition duration-250 ease-linear focus:ring focus:ring-opacity-50 focus:ring-magpie-200", {
                                      "opacity-60": loading || !newPassword || !confirmPassword || newPassword !== confirmPassword,
                                      "opacity-100 cursor-pointer": !loading && newPassword && confirmPassword && newPassword === confirmPassword
                                    })} onClick={onResetPassword}>
                                      <div className="flex align-baseline flex-row-reverse w-full relative">
                                        <div className="flex align-baseline flex-row-reverse justify-center w-full leading-0 flex-1-auto">
                                          <span className={cn("block text-white text-base font-medium leading-7 proportional-nums whitespace-nowrap transition duration-250 ease-linear -mt-px", {
                                            "opacity-100": !loading,
                                            "opacity-0": loading
                                          })}>Continue</span>
                                          <div className={cn("spinner-icon block absolute top-1/2 transform -translate-y-1/2 opacity-0 transition-all duration-200 -mt-px", {
                                            'opacity-0': !loading,
                                            'opacity-100': loading
                                          })}>
                                            <div className="w-5 h-5">
                                              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false">
                                                <ellipse className="origin-center animate-spinner-spin" cx="12" cy="12" rx="10" ry="10" style={{ stroke: 'rgb(255, 255, 255)' }}></ellipse>
                                              </svg>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </button>
                                    <AnimateHeight
                                      height={resetError ? 'auto' : 0}
                                      animateOpacity
                                      className="transition-height duration-300 ease-in-out"
                                    >
                                      <div className="pt-2 pointer-events-auto">
                                        <div className="pt-1 pl-px">
                                          <div className="flex items-center flex-row relative">
                                            <div className="flex-initial pr-1">
                                              <div className="flex text-red-500">
                                                <svg className="w-4 h-4 m" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                                              </div>
                                            </div>
                                            <span className="text-red-500 inline text-sm font-medium proportional-nums whitespace-normal">{resetError}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </AnimateHeight>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
              
              
              <div className="flex-grow flex-shrink"></div>
              <div className="relative py-6 px-5">
                <div className="pointer-events-none">
                  <div className="flex flex-row justify-center flex-nowrap -ml-2 -mt-2">
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <a className="text-sm font-medium hover:text-gray-900" href="https://magpie.im" target="_self">&copy; Magpie.IM</a>
                    </div>
                    <div className="mt-2 ml-2 pointer-events-auto">
                      <span className="text-gray-50">·</span>
                    </div>
                    <div className="mt-2 ml-2 pointer-events-auto desktop:hidden">
                      <a className="text-sm font-medium hover:text-gray-900" href="https://magpie.im/terms.html" target="_self">Privacy &amp; terms</a>
                    </div>
                    <div className="hidden mt-2 ml-2 pointer-events-auto desktop:block">
                      <a className="text-sm font-medium hover:text-gray-900" href="https://magpie.im/terms.html" target="_self">Terms &amp; conditions</a>
                    </div>
                    <div className="hidden mt-2 ml-2 pointer-events-auto desktop:block">
                      <span className="text-gray-50">·</span>
                    </div>
                    <div className="hidden mt-2 ml-2 pointer-events-auto desktop:block">
                      <a className="text-sm font-medium hover:text-gray-900" href="https://magpie.im/privacy.html" target="_self">Privacy policy</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Cookie Banner */}
        <div></div>
      </div>
    )
  }

  return (
    <div className="h-screen bg-white">
      <Head>
        <title>Magpie: Reset Password</title>
        <link rel="stylesheet" href="/css/auth.css"/>
      </Head>
      <div className="h-full min-h-full bg-white flex flex-col">
        {/* Background */}
        <div>
          <div className="fixed left-0 right-0 -top-62 bottom-0 z-0 transform -skew-y-12">
            <div className="absolute -top-250 left-0 right-0 origin-0-50 h-425 bg-offset"></div>
          </div>
        </div>
        {/* Content */}
        <div className="my-0 mx-4 z-0 relative flex flex-row h-full pt-14 justify-center">
          <div className="flex flex-col w-96 desktop:w-1/2 desktop:min-w-1/2 ultraWide:w-135 ultraWide:min-w-135">
            <div className="pb-10 pl-5">
              <img className="h-10 w-auto" src="https://client-objects.s3-us-west-2.amazonaws.com/magpie/magpie-logo-brand.svg" alt="magpie" />
            </div>
            <div className="flex-shrink-0 bg-white overflow-hidden shadow-card-lg rounded">
              <div className="py-8 px-0 desktop:py-14 desktop:px-12">
                <div>
                  <div className="px-5">
                    <div role="main">
                      {
                        sendSuccess && !verifying &&
                        <div className="pointer-events-none">
                          <div className="flex flex-col justify-start flex-nowrap">
                            <div className="pointer-events-auto">
                              <div className="pb-3 pointer-events-none">
                                <div className="flex flex-col justify-start flex-nowrap -mt-6 -ml-6">
                                  <div className="mt-6 ml-6 pointer-events-auto">
                                    <div className="pointer-events-none">
                                      <div className="flex flex-col justify-start flex-nowrap -ml-2 -mt-2">
                                        <div className="mt-2 ml-2 pointer-events-auto">
                                          <h1 className="text-2xl font-bold tracking-tight proportional-nums">Thanks, check your email for instructions to reset your password</h1>
                                        </div>
                                        <div className="mt-2 ml-2 pointer-events-auto">
                                          <span className="inline text-sm proportional-nums whitespace-normal">Didn't get the email? Check your spam folder or <a className="text-magpie-600 font-medium cursor-pointer" onClick={(e) => onResendClick(e)}>resend</a>.</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                      {
                        !sendSuccess && verifying &&
                        <div className="pointer-events-none">
                          <div className="flex flex-col justify-start flex-nowrap">
                            <div className="pointer-events-auto">
                              <div className="pb-3 pointer-events-none">
                                <div className="flex flex-col justify-start flex-nowrap -mt-6 -ml-6">
                                  <div className="mt-6 ml-6 pointer-events-auto">
                                    <div className="pointer-events-none">
                                      <div className="flex flex-col justify-start flex-nowrap -ml-2 -mt-2">
                                        <div className="mt-2 ml-2 pointer-events-auto">
                                          <h1 className="text-2xl font-bold tracking-tight proportional-nums">Making sure you're not a robot...</h1>
                                        </div>
                                        <div className="mt-2 ml-2 pointer-events-auto">
                                          <span className="inline text-sm proportional-nums whitespace-normal">This helps prevent robots (and other nefarious machines) from accessing your Magpie account.</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                      {
                        !sendSuccess && !verifying && 
                        <>
                          <div className="mb-5">
                            <h1 className="text-2xl font-bold tracking-tight proportional-nums">Reset your password</h1>
                          </div>
                          <div className="w-full pb-3 pointer-events-none">
                            <div className="flex flex-col justify-start flex-nowrap -ml-6 -mt-6">
                              <div className="ml-6 mt-6 pointer-events-auto">
                                <div className="pointer-events-none">
                                  <div className="flex flex-col justify-start flex-nowrap -ml-3 -mt-3">
                                    <div className="ml-3 mt-3 pointer-events-auto">
                                      <span className="inline text-sm proportional-nums whitespace-normal">Enter the email address associated with your account and we'll send you a link to reset your password.</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="ml-6 mt-6 pointer-events-auto">
                                <div className="pointer-events-none">
                                  <div className="flex flex-col justify-start flex-nowrap -ml-3 -mt-3">
                                    <div className="ml-3 mt-3 pointer-events-auto">
                                      <label htmlFor="email" className="cursor-pointer inline text-sm font-medium proportional-nums whitespace-normal">Email</label>
                                    </div>
                                    <div className="ml-3 mt-3 pointer-events-auto">
                                      <input ref={emailRef} id="email" name="email" type="email" autoComplete="email" autoCapitalize="none" aria-invalid={emailError} className={cn("inline-flex w-full max-w-full cursor-text rounded min-h-11 text-gray-900 text-base leading-7 bg-white border select-auto focus:ring focus:ring-opacity-50", {
                                        "border-gray-200 focus:outline-none focus:border-magpie-300 focus:ring-magpie-200": !emailError,
                                        "border-red-500 focus:outline-none focus:border-red-500 focus:ring-red-300": emailError
                                      })} value={email} onChange={(e) => onEmailChange(e)} />
                                    </div>
                                    <AnimateHeight
                                      height={resetError ? 'auto' : 0}
                                      animateOpacity
                                      className="transition-height duration-300 ease-in-out"
                                    >
                                      <div className="ml-3 mt-3 pointer-events-auto">
                                        <div className="pt-1 pl-px">
                                          <div className="flex items-center flex-row relative">
                                            <div className="flex-initial pr-1">
                                              <div className="flex text-red-500">
                                                <svg className="w-4 h-4 m" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                                              </div>
                                            </div>
                                            <span className="text-red-500 inline text-sm font-medium proportional-nums whitespace-normal">{resetError}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </AnimateHeight>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-6 ml-6 pointer-events-auto">
                                <button type="submit" disabled={loading} className={cn("py-2 px-4 rounded min-h-11 h-full w-full inline-flex text-base leading-7 font-medium text-white border-none bg-magpie-500 focus:outline-none transition duration-250 ease-linear focus:ring focus:ring-opacity-50 focus:ring-magpie-200", {
                                  "opacity-60": loading,
                                  "opacity-100 cursor-pointer": !loading
                                })} onClick={onForgotPassword}>
                                  <div className="flex align-baseline flex-row-reverse w-full relative">
                                    <div className="flex align-baseline flex-row-reverse justify-center w-full leading-0 flex-1-auto">
                                      <span className={cn("block text-white text-base font-medium leading-7 proportional-nums whitespace-nowrap transition duration-250 ease-linear -mt-px", {
                                        "opacity-100": !loading,
                                        "opacity-0": loading
                                      })}>Continue</span>
                                      <div className={cn("spinner-icon block absolute top-1/2 transform -translate-y-1/2 opacity-0 transition-all duration-200 -mt-px", {
                                        'opacity-0': !loading,
                                        'opacity-100': loading
                                      })}>
                                        <div className="w-5 h-5">
                                          <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false">
                                            <ellipse className="origin-center animate-spinner-spin" cx="12" cy="12" rx="10" ry="10" style={{ stroke: 'rgb(255, 255, 255)' }}></ellipse>
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="pt-2 pointer-events-none">
                            <div className="flex flex-col justify-start flex-nowrap">
                              <div className="pointer-events-auto">
                                <div className="flex flex-row relative">
                                  <div className="flex flex-row justify-center flex-1-auto leading-0">
                                    <Link href="/login">
                                      <a className="inline text-sm font-medium proportional-nums whitespace-normal text-magpie-600">Return to sign in</a>
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {
              !sendSuccess && !verifying &&
              <div className="pt-8 px-5 pointer-events-none">
                <div className="flex flex-row justify-center flex-nowrap -mt-1 -ml-1">
                  <div className="mt-1 ml-1 pointer-events-auto">
                    <span className="text-sm text-gray-400">Don't have an account? <Link href="/register"><a className="font-medium text-body hover:text-gray-900 no-underline">Sign up</a></Link></span>
                  </div>
                </div>
              </div>
            }
            <div className="flex-grow flex-shrink"></div>
            <div className="relative py-6 px-5">
              <div className="pointer-events-none">
                <div className="flex flex-row justify-center flex-nowrap -ml-2 -mt-2">
                  <div className="mt-2 ml-2 pointer-events-auto">
                    <a className="text-sm font-medium hover:text-gray-900" href="https://magpie.im" target="_self">&copy; Magpie.IM</a>
                  </div>
                  <div className="mt-2 ml-2 pointer-events-auto">
                    <span className="text-gray-50">·</span>
                  </div>
                  <div className="mt-2 ml-2 pointer-events-auto desktop:hidden">
                    <a className="text-sm font-medium hover:text-gray-900" href="https://magpie.im/terms.html" target="_self">Privacy &amp; terms</a>
                  </div>
                  <div className="hidden mt-2 ml-2 pointer-events-auto desktop:block">
                    <a className="text-sm font-medium hover:text-gray-900" href="https://magpie.im/terms.html" target="_self">Terms &amp; conditions</a>
                  </div>
                  <div className="hidden mt-2 ml-2 pointer-events-auto desktop:block">
                    <span className="text-gray-50">·</span>
                  </div>
                  <div className="hidden mt-2 ml-2 pointer-events-auto desktop:block">
                    <a className="text-sm font-medium hover:text-gray-900" href="https://magpie.im/privacy.html" target="_self">Privacy policy</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={siteKey || ''}
          size="invisible"
        />
      </div>
      {/* Cookie Banner */}
      <div></div>
    </div>
  )
}

export default ResetPassword